// @flow
import styled, { css } from "styled-components";
import {
  mediumHeading,
  mediumParagraph,
  sectionHeading,
  smallHeading,
  PlainText,
  media,
} from "@nested/brand";
import { SmartLink } from "@nested/component-library";

const headerPaddingStyles = css`
  margin: 0px;
  padding-top: ${({ hideCategory }) => (hideCategory ? "20px" : "0")};
`;

const H3 = styled.h3`
  ${smallHeading}
  ${media.tablet`
    ${mediumHeading}
  `};
  ${headerPaddingStyles}
`;

const H4 = styled.h4`
  ${smallHeading}
  ${headerPaddingStyles}
`;

type Props = {
  hideCategory?: boolean,
  featured?: boolean,
  post: PrismicPost,
};

const Card = styled.div`
  overflow: hidden;
  margin-bottom: 40px;
`;

const FeaturedImage = styled.div`
  background-image: url("${({ image }) => image}");
  background-size: cover;
  background-position: center;
  border-radius: 5px;
  height: 0px;
  padding-bottom: 50%;
`;

const SummaryText = styled.p`
  line-clamp: ${({ featured }) => (featured ? 2 : 3)};
  -webkit-line-clamp: ${({ featured }) => (featured ? 2 : 3)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  max-height: 100px;
  ${mediumParagraph}
`;

const linkStyles = css`
  text-decoration: none;
  font-family: ${({ theme }) => theme.fonts.striverText};
`;

const CategoryLink = styled.a`
  ${sectionHeading}
  color: ${({ theme }) => theme.palette.hague40};
`;

const ReadMore = styled.span`
  ${mediumParagraph}
  color: ${({ theme }) => theme.palette.terracotta100};
  font-weight: 500;
`;

const PostCard = ({ post, featured, hideCategory }: Props) => {
  const Heading = featured ? H3 : H4;
  return (
    <Card hideCategory={hideCategory} featured={featured}>
      <SmartLink css={linkStyles} to={`/blog/posts/${post.uid}`}>
        <FeaturedImage featured={featured} image={post.data.hero_image.url} />
      </SmartLink>
      {hideCategory || (
        <div
          css={css`
            padding-top: 20px;
          `}
        >
          <CategoryLink
            to={`/blog/categories/${post.data.category.uid}`}
            colour="honey"
            shade="light"
          >
            {post.data.category.data.name}
          </CategoryLink>
        </div>
      )}
      <div>
        <Heading hideCategory={hideCategory} data-test="post-card-title" left>
          <SmartLink css={linkStyles} to={`/blog/posts/${post.uid}`}>
            <PlainText css={mediumHeading}>{post.data.title}</PlainText>
          </SmartLink>
        </Heading>
      </div>
      <SmartLink css={linkStyles} to={`/blog/posts/${post.uid}`}>
        <SummaryText featured={featured} left>
          {post.data.summary}
        </SummaryText>
        <ReadMore>Read more</ReadMore>
      </SmartLink>
    </Card>
  );
};

export { PostCard };
